"use client";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { TextPlugin } from "gsap/dist/TextPlugin";
import React, { useEffect } from "react";
import FullSection from "@/components/FullSection";
import HeroSection from "@/components/HeroSection";
import HorizontalSlider from "@/components/HorizontalSlider";
import Logo from "@/components/Logo";
import VerticalSlider from "@/components/VerticalSlider";
import { useNavbarColor } from "@/context/NavbarColorContext";
import { useBreakpoint } from "@/lib/breakpoints";

gsap.registerPlugin(useGSAP, ScrollTrigger, TextPlugin, ScrollToPlugin);

export default function HomePage({ homepageData }: { homepageData: any }) {
  const pageData = homepageData;
  const { isDesktop } = useBreakpoint();
  const { setIsWhite } = useNavbarColor();

  useEffect(() => {
    ScrollTrigger.refresh();
  }, []);

  useEffect(() => {
    setIsWhite(true);
  }, [setIsWhite]);

  useGSAP(
    () => {
      const master = gsap.timeline();
      const pinDuration = isDesktop ? 3000 : 200;
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: "#clipAnimationContainer",
          start: isDesktop ? "center center" : "top 50%",
          end: `+=${pinDuration}`,
          scrub: 1,
          pin: isDesktop,
          invalidateOnRefresh: true,
        },
      });
      tl.to("#clipAnimation", {
        autoAlpha: 1,
        duration: isDesktop ? 0.05 : 1,
      });
      if (isDesktop) {
        tl.to(
          "#clipAnimation",
          {
            clipPath:
              "path('M1147.03 145.115C1089.07 112.306 1081.34 28.392 1237.84 0H0V512.441V722.419H25.7615H158.433C246.023 722.419 229.922 612.636 291.749 620.838C353.577 629.041 513.297 778.572 480.451 722.419C447.605 666.266 508.146 694.658 602.82 597.494C697.493 500.33 665.29 528.722 727.118 597.494C788.945 666.266 826.638 576.86 951.991 553.965C1103.98 526.204 1068.33 700.16 1158.8 632.083C1203.24 598.643 1109.03 341.966 1215.94 377.929C1322.85 413.892 1324.14 338.811 1373.73 246.064C1423.32 153.317 1204.99 177.923 1147.03 145.115Z')",
          },
          "<",
        );
      }

      master.add(tl);
      if (isDesktop) {
        const pinBG = gsap.timeline({
          scrollTrigger: {
            trigger: "#clipAnimationContainer",
            start: "center center",
            end: `+=${pinDuration}`,
            scrub: true,
            pin: "#mainBg",
            pinSpacing: false,
          },
        });
        master.add(pinBG, "<");
      }
    },
    { dependencies: [isDesktop], revertOnUpdate: true },
  );

  useGSAP(() => {
    const fadeElements: HTMLElement[] = gsap.utils.toArray(".fade-element");
    fadeElements.forEach((el) => {
      gsap.from(el, {
        autoAlpha: 0,
        y: 50,
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          end: "+=300",
          scrub: 1,
        },
      });
    });
  });

  return (
    <>
      <HeroSection
        video={pageData?.HeroSection?.Video?.data?.attributes?.url}
        title={pageData?.HeroSection?.Title}
        text={pageData?.HeroSection?.Description}
        image={pageData?.HeroSection?.Image.data.attributes}
      />

      <div
        id="clipAnimationContainer"
        className="container relative min-h-screen flex-center gap-12"
      >
        <div className="relative w-fit max-w-[1100px]">
          <div className="seperator absolute -top-10 border-t-black"></div>
          <h2 className="3xl:text-[60px] text-white text-xl lg:text-[50px] lg:leading-none">
            {pageData?.SectionTwo?.Text}
          </h2>
          <h2
            id="clipAnimation"
            className="absolute top-0 3xl:text-[60px] text-darkGray text-xl opacity-0 lg:text-[50px] lg:leading-none"
          >
            {pageData?.SectionTwo?.Text}
          </h2>
        </div>
      </div>

      <VerticalSlider data={pageData} className="container" />

      <HorizontalSlider data={pageData} />

      <div className="mt-40">
        <FullSection
          img={pageData?.SectionFive?.Image?.data?.attributes}
          title={pageData?.SectionFive?.Title}
          text={pageData?.SectionFive?.Descriotion}
          link={pageData?.SectionFive?.Link}
          imgPosition="object-left"
        />
      </div>
      <div className="fade-element mb-20 px-5 lg:mb-40">
        <p className="mt-10 text-center">{pageData?.SectionFive?.EndText}</p>
      </div>

      
    </>
  );
}
